import React, { ReactNode, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Fuse from 'fuse.js'
import { useParams } from 'react-router-dom'

import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ViewBase } from '../../../../components/common/ViewBase'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { BinderList } from '../../../../components/society/Binder/BinderList/BinderList'
import { binderQueries } from '../../../../api/binder'
import { IBinderModel } from '../../../../interfaces/models/binder.interfaces'
import { unitQueries } from '../../../../api/units'
import { unitString } from '../../../../hooks/useUnitsString'
import { entranceQueries } from '../../../../api/society-entrances'
import {
  ButtonProps,
  ButtonVariant,
} from '../../../../components/common/Button'
import { IconChoices } from '../../../../components/common/Icon'
import { useModal } from '../../../../hooks/useModal'
import { CreateUpdateBinderModal } from '../../../../components/society/Binder/CreateUpdateBinderModal'
import { CreateUpdateBinderDocumentModal } from '../../../../components/society/Binder/CreateUpdateBinderDocumentModal'
import { UploadBinderModal } from '../BinderPickerView/UploadBinderModal'
import { BinderType } from '../BinderPickerView/helper'

export const BinderView = (): ReactNode => {
  const { translate } = useAppTranslation()
  const { folderId } = useParams()
  const [searchString, setSearchString] = useState('')
  const { society } = useCurrentSociety()
  const { unitId } = useParams()
  const [editFolderDocument, setEditFolderDocument] = useState(false)
  const {
    show: showCreateFolderModal,
    open: openCreateFolderModal,
    close: closeCreateFolderModal,
  } = useModal()
  const {
    show: showCreateDocumentModal,
    open: openCreateDocumentModal,
    close: closeCreateDocumentModal,
  } = useModal()
  const {
    show: showCreateBinderModal,
    open: openCreateBinderModal,
    close: closeCreateBinderModal,
  } = useModal()

  const { data: bindersData, isLoading: isLoadingBinders } = useQuery(
    binderQueries.getBinderPerUnit(society?._id || '', unitId || '', folderId)
  )
  const binders = bindersData?.binderData
  const binderId = bindersData?.binderId || ''
  const isPublished = !!bindersData?.publishedAt

  const { data: units } = useQuery(
    unitQueries.getUnitsPerSociety(society?._id || '')
  )
  const societyUnits = units?.filter((su) => su.societyId === society?._id)
  const unit = societyUnits?.find((su) => su._id === unitId)

  const { data: societyEntrences } = useQuery(
    entranceQueries.getEntrancesPerSociety(society?._id)
  )

  const _unitString = unitString(
    unit ? [unit] : [],
    society ? [society] : [],
    societyEntrences || [],
    true
  )

  const handleEditModal = (): void => {
    setEditFolderDocument(true)
    openCreateFolderModal()
  }

  const handleCreateModal = (): void => {
    setEditFolderDocument(false)
    openCreateFolderModal()
  }

  const rootRoute = 'management:binder-unit'
  const documentRoute = 'management:binder-detail'

  const onChangeSearchString = (
    event: React.FormEvent<HTMLInputElement>
  ): void => {
    setSearchString(event.currentTarget.value)
  }

  const getDocuments = (): IBinderModel[] => {
    return folderId
      ? binders?.filter((doc) => doc.parentId === folderId) || []
      : binders?.filter((doc) => doc.parentId == null) || []
  }
  const documents = getDocuments()

  const isRoot = !folderId
  const currentDocument = folderId
    ? binders?.find((doc) => doc._id === folderId)
    : undefined

  useDocumentTitle(
    isRoot
      ? reverseDocumentTitle('management:binder-unit', {
          '{{ unitName }}': _unitString,
        })
      : reverseDocumentTitle('management:binder-detail', {
          '{{ societyName }}': society?.name,
          '{{ documentName }}': currentDocument?.name,
        })
  )

  const fuseOptions = {
    threshold: 0.3,
    keys: ['name'],
  }
  const fuse = new Fuse(documents, fuseOptions)

  const searchDocuments = fuse.search(searchString).map((result) => result.item)

  const getButtons = (): ButtonProps[] => {
    let buttons: ButtonProps[] = folderId
      ? [
          {
            label: translate('documentsView.editFolder'),
            variant: ButtonVariant.DEFAULT,
            icon: IconChoices.EDIT,
            onClick: () => handleEditModal(),
          },
        ]
      : []
    buttons = [
      ...buttons,
      {
        label: translate('managementBinderView.addNewFolder'),
        variant: ButtonVariant.PRIMARY,
        icon: IconChoices.FOLDER,
        iconSize: 20,
        onClick: () => handleCreateModal(),
      },
      {
        label: translate('managementBinderView.uploadFoldersBtn'),
        variant: ButtonVariant.PRIMARY,
        icon: IconChoices.DOCUMENT_ADD,
        onClick: openCreateBinderModal,
      },
      {
        label: translate('managementBinderView.uploadDocumentsBtn'),
        variant: ButtonVariant.PRIMARY,
        icon: IconChoices.DOCUMENT_ADD,
        onClick: () => openCreateDocumentModal(),
      },
    ]
    return buttons
  }

  const binderObject: BinderType = {
    adress: _unitString,
    aptNumber: unit?.titleLegal ?? '',
    floor: unit?.floor ?? 0,
    unitId: unitId || '',
  }

  return (
    <ErrorBoundary>
      <ViewBase
        searchBar={{ value: searchString, onChange: onChangeSearchString }}
        title={translate('binderView.title')}
        subtitle={_unitString}
        buttons={getButtons()}
        loading={isLoadingBinders}
      >
        <BinderList
          rootRoute={rootRoute}
          folderId={folderId}
          documentRoute={documentRoute}
          documents={searchString === '' ? documents : searchDocuments}
          currentDocument={currentDocument}
          binder={binders || []}
          noSearchResults={searchString !== '' && searchDocuments.length === 0}
          unitId={unitId}
          isAdminView
          binderId={binderId}
          section="admin"
        />
      </ViewBase>
      {showCreateFolderModal && (
        <CreateUpdateBinderModal
          show={showCreateFolderModal}
          close={closeCreateFolderModal}
          parentId={isRoot ? undefined : folderId}
          id={editFolderDocument ? folderId : undefined}
          binderId={binderId}
          published={isPublished}
        />
      )}
      {showCreateDocumentModal && (
        <CreateUpdateBinderDocumentModal
          show={showCreateDocumentModal}
          close={closeCreateDocumentModal}
          parentId={isRoot ? undefined : folderId}
          binderId={binderId}
          unitId={unitId}
          published={isPublished}
        />
      )}
      {showCreateBinderModal && (
        <UploadBinderModal
          rows={[binderObject]}
          show={showCreateBinderModal}
          close={closeCreateBinderModal}
          societyId={society?._id as string}
          folderId={folderId}
          published={isPublished}
        />
      )}
    </ErrorBoundary>
  )
}
